// Opportunities view (BUILD_SPEC §6).
// Two sub-tabs:
//   Warm — regionals with >=1 Apollo customer AND >=1 opportunity.
//          Sister-property pitch list. Highest conversion targets.
//   Cold — regionals with 0 Apollo coverage. Cold-call targets.

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { usePhoneLimit, ShowMore } from '../../mobile';
import DirectoryTopNav from '../components/DirectoryTopNav';
import PropertyRow from '../components/PropertyRow';

export default function DirectoryOpportunitiesPage() {
  const { isSuperAdmin } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get('kind') === 'cold' ? 'cold' : 'warm';

  const [warm, setWarm] = useState(null);
  const [cold, setCold] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [openIds, setOpenIds] = useState(() => new Set());
  const [exporting, setExporting] = useState(false);

  async function onExport() {
    setExporting(true);
    try {
      await api.downloadDirectoryCsv({ view: tab, q: filter || undefined });
    } catch (err) {
      alert(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    Promise.all([
      api.getDirectoryOpportunities('warm'),
      api.getDirectoryOpportunities('cold'),
    ])
      .then(([w, c]) => { if (!cancelled) { setWarm(w.items || []); setCold(c.items || []); } })
      .catch(err => { if (!cancelled) setError(err?.responseBody?.error || err.message || 'Failed to load opportunities'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const items = tab === 'warm' ? warm : cold;

  const visible = useMemo(() => {
    if (!items) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it =>
      (it.regional_name || '').toLowerCase().includes(q) ||
      (it.company_name || '').toLowerCase().includes(q)
    );
  }, [items, filter]);
  // Long regional lists on a phone: first 40 + Show more.
  const { shown, rest, more } = usePhoneLimit(visible, visible);

  function setTab(next) {
    setOpenIds(new Set());
    setFilter('');
    setParams(next === 'cold' ? { kind: 'cold' } : {}, { replace: true });
  }

  function toggleOpen(id) {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  return (
    <div className="directory-page">
      <DirectoryTopNav />

      <div className="directory-opps__header">
        <h1 className="directory-companies__title">Opportunities</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <p className="directory-companies__count">
            {loading ? 'Loading...' : tab === 'warm'
              ? `${visible.length} of ${warm?.length || 0} warm regionals`
              : `${visible.length} of ${cold?.length || 0} cold regionals`}
          </p>
          {isSuperAdmin && !loading ? (
            <button
              type="button"
              className="btn btn-sm directory-export-btn"
              onClick={onExport}
              disabled={exporting}
            >
              {exporting ? 'Exporting...' : `Export ${tab === 'warm' ? 'Warm' : 'Cold'} CSV`}
            </button>
          ) : null}
        </div>
      </div>

      <div className="directory-opps__subtabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'warm'}
          className={'directory-opps__subtab' + (tab === 'warm' ? ' directory-opps__subtab--active' : '')}
          onClick={() => setTab('warm')}
        >
          Warm
          <span className="directory-opps__subtab-count">
            {warm ? warm.length : '...'}
          </span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'cold'}
          className={'directory-opps__subtab' + (tab === 'cold' ? ' directory-opps__subtab--active' : '')}
          onClick={() => setTab('cold')}
        >
          Cold
          <span className="directory-opps__subtab-count">
            {cold ? cold.length : '...'}
          </span>
        </button>
      </div>

      <p className="directory-opps__blurb">
        {tab === 'warm'
          ? 'Regionals where Apollo already serves at least one property. Use the sister-property pitch: "We already tow at X — let\u2019s do Y too."'
          : 'Regionals with zero Apollo coverage. These are cold-call targets, ranked by property count.'}
      </p>

      <input
        type="search"
        className="directory-companies__filter"
        style={{ marginBottom: 16, maxWidth: 480 }}
        placeholder={tab === 'warm' ? 'Filter by regional or company...' : 'Filter cold regionals or companies...'}
        value={filter}
        onChange={e => setFilter(e.target.value)}
      />

      {error ? (
        <div className="card" style={{ padding: 16, color: '#dc2626' }}>{error}</div>
      ) : null}

      {tab === 'warm' ? (
        <WarmList items={shown} openIds={openIds} onToggle={toggleOpen} />
      ) : (
        <ColdList items={shown} />
      )}
      <ShowMore rest={rest} onMore={more} />
    </div>
  );
}

function WarmList({ items, openIds, onToggle }) {
  if (!items.length) return <EmptyState />;
  return (
    <div className="directory-accordion">
      {items.map(it => {
        const open = openIds.has(it.regional_id);
        return (
          <div key={it.regional_id} className="directory-accordion__group">
            <button
              type="button"
              className="directory-accordion__toggle"
              aria-expanded={open}
              onClick={() => onToggle(it.regional_id)}
            >
              <span className="directory-accordion__chev" aria-hidden="true">{open ? '\u25BC' : '\u25B6'}</span>
              <span className="directory-accordion__name">
                <span style={{ fontWeight: 700 }}>{it.regional_name}</span>
                <Link
                  to={`/sales/directory/companies/${encodeURIComponent(it.company_id)}`}
                  className="directory-opps__company-link"
                  onClick={e => e.stopPropagation()}
                >
                  {it.company_name}
                </Link>
              </span>
              <span className="directory-accordion__stats">
                <strong>{it.apollo_customer_count}</strong> of <strong>{it.property_count}</strong> ({it.coverage_pct}%) &middot;{' '}
                <span className="directory-opps__opps-tag">{it.opportunity_count} opps</span>
              </span>
            </button>
            {open ? (
              <div className="directory-accordion__body">
                <div className="directory-regional-book" style={{ maxHeight: 'none' }}>
                  {(it.properties || []).map(p => <PropertyRow key={p.id} property={p} />)}
                </div>
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function ColdList({ items }) {
  if (!items.length) return <EmptyState />;
  return (
    <div className="directory-cold-list">
      {items.map(it => (
        <Link
          key={it.regional_id}
          to={`/sales/directory/companies/${encodeURIComponent(it.company_id)}`}
          className="directory-cold-row"
        >
          <div className="directory-cold-row__main">
            <span className="directory-cold-row__regional">{it.regional_name}</span>
            <span className="directory-cold-row__company">{it.company_name}</span>
          </div>
          <div className="directory-cold-row__count">
            <span className="directory-cold-row__count-value">{it.property_count}</span>
            <span className="directory-cold-row__count-label">{it.property_count === 1 ? 'property' : 'properties'}</span>
          </div>
          <div className="directory-cold-row__cities">
            {(it.cities || []).slice(0, 5).join(', ')}
            {(it.cities || []).length > 5 ? ` +${it.cities.length - 5}` : ''}
          </div>
        </Link>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="directory-companies__empty">No regionals match your filter.</div>
  );
}
